import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Navbar from '../components/Navbar';
import { Video, Mic, Monitor, Camera, ArrowLeft, Radio } from 'lucide-react';
import { tagOptions } from '../data/mockData';

export default function GoLivePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const videoRef = useRef(null);
  const [title, setTitle] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [stream, setStream] = useState(null);
  const [devices, setDevices] = useState({ cameras: [], mics: [] });
  const [selectedCamera, setSelectedCamera] = useState('');
  const [selectedMic, setSelectedMic] = useState('');
  const [cameraError, setCameraError] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/auth'); return; }
    initCamera();
    return () => { if (stream) stream.getTracks().forEach(t => t.stop()); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initCamera = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setStream(s);
      if (videoRef.current) videoRef.current.srcObject = s;
      const devs = await navigator.mediaDevices.enumerateDevices();
      setDevices({
        cameras: devs.filter(d => d.kind === 'videoinput'),
        mics: devs.filter(d => d.kind === 'audioinput'),
      });
    } catch (err) {
      console.warn('Camera not available:', err);
      setCameraError(true);
    }
  };

  const toggleTag = (tag) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const handleGoLive = () => {
    if (!title.trim()) return;
    const roomId = `room-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    // Stop the preview stream before navigating - LiveRoomPage will get its own
    if (stream) stream.getTracks().forEach(t => t.stop());
    navigate(`/room/${roomId}`, {
      state: { isCreator: true, title, tags: selectedTags }
    });
  };

  return (
    <div data-testid="go-live-page" className="min-h-screen bg-[#050108]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 pt-24 pb-12">
        <button data-testid="back-btn" onClick={() => navigate('/home')}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6">
          <ArrowLeft className="w-5 h-5" /> Back
        </button>

        <h1 className="font-display text-2xl font-bold mb-6">Create a Room</h1>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Camera Preview */}
          <div className="glass-card rounded-3xl p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Camera className="w-5 h-5 text-[#FF007F]" /> {t('preview')}
            </h3>
            <div className="aspect-video rounded-2xl overflow-hidden bg-[#0a0512] relative">
              {cameraError ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400">
                  <Video className="w-12 h-12 mb-3 opacity-50" />
                  <p className="text-sm">Camera not available</p>
                  <p className="text-xs mt-1 text-slate-500">Test environment - no camera detected</p>
                </div>
              ) : (
                <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
              )}
            </div>

            {/* Device Selection */}
            {!cameraError && (
              <div className="mt-4 space-y-3">
                {devices.cameras.length > 0 && (
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">{t('selectCamera')}</label>
                    <select value={selectedCamera} onChange={e => setSelectedCamera(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none">
                      {devices.cameras.map(c => <option key={c.deviceId} value={c.deviceId}>{c.label || 'Camera'}</option>)}
                    </select>
                  </div>
                )}
                {devices.mics.length > 0 && (
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">{t('selectMic')}</label>
                    <select value={selectedMic} onChange={e => setSelectedMic(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none">
                      {devices.mics.map(m => <option key={m.deviceId} value={m.deviceId}>{m.label || 'Microphone'}</option>)}
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Room Setup */}
          <div className="glass-card rounded-3xl p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Monitor className="w-5 h-5 text-[#FF007F]" /> Room Setup
            </h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">{t('roomTitle')}</label>
                <input data-testid="room-title-input" type="text" value={title} onChange={e => setTitle(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:border-[#FF007F]/50"
                  placeholder="e.g. Friday Night Vibes" maxLength={60} />
                <p className="text-xs text-slate-500 mt-1">{title.length}/60</p>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-2 block">{t('tags')}</label>
                <div className="flex flex-wrap gap-2">
                  {tagOptions.map(tag => (
                    <button key={tag} onClick={() => toggleTag(tag)}
                      className={`tag-pill ${selectedTags.includes(tag) ? 'active' : ''}`}>
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <button data-testid="start-live-btn" onClick={handleGoLive}
              disabled={!title.trim()}
              className="w-full mt-8 gradient-button rounded-full py-4 font-semibold text-lg flex items-center justify-center gap-3 disabled:opacity-40 disabled:cursor-not-allowed">
              <Radio className="w-5 h-5" />
              Create & Join Room
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
